"use client"

import { useEffect, type ReactNode } from "react"
import Image from "next/image"
import Link from "next/link"
import { useCountry } from "@/components/layout/country-provider"
import { CtaBanner } from "@/components/ui/cta-banner"
import { type ArticleData } from "@/data/articles"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { track } from "@/lib/analytics"
import type { LexicalContent, CmsAuthor, FaqItem } from "@/lib/cms"
import {
  RenderLexical,
  type LexicalRenderOptions,
} from "@/components/cms/lexical/render-lexical"
import { FaqAccordion } from "@/components/ui/faq-accordion"
import { rewriteDeadLink } from "@/lib/dead-link-rewrites"
import {
  isPathAllowedInCountry,
  gateForPath,
  countriesForGatedPage,
} from "@/lib/country-gates"
import type { Country } from "@/i18n/config"
import { safeUrl } from "@/lib/url"
import { cmsMediaUrl, cmsLocale } from "@/lib/cms"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { ArrowLeftIcon } from "@/components/ui/icons"
import { blobMediaUrl } from "@/lib/blob-url"
import {
  IMAGE_PLACEHOLDER_RE,
  RAW_IMG_TAG_RE,
} from "@/lib/edit/lexical-image-placeholders"
import { useEditRegion } from "@/lib/edit/edit-region-registry"

/* ───────────────────── Helpers ───────────────────── */

const CARD_BG: Record<ArticleData["cardColor"], string> = {
  aubergine: "bg-aubergine-900",
  olive: "bg-light-green-900",
  lavender: "bg-[#6B5B8A]",
  white: "bg-white",
}

function isLightCard(color: ArticleData["cardColor"]): boolean {
  return color === "white"
}

/* ───────────────────── Share button ───────────────────── */

function ShareButton() {
  function handleShare() {
    if (typeof navigator !== "undefined" && navigator.share) {
      navigator.share({ url: window.location.href }).catch(() => {})
    } else if (typeof navigator !== "undefined") {
      navigator.clipboard.writeText(window.location.href).catch(() => {})
    }
  }

  return (
    <button
      type="button"
      onClick={handleShare}
      className="cursor-pointer rounded-full p-2 transition-colors hover:bg-neutral-100"
      aria-label="Share article"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
        <polyline points="16 6 12 2 8 6" />
        <line x1="12" y1="2" x2="12" y2="15" />
      </svg>
    </button>
  )
}

/* ───────────────────── Related card ───────────────────── */

function RelatedCard({
  article,
  index: _index,
}: {
  article: ArticleData
  index: number
}) {
  const light = isLightCard(article.cardColor)
  const lp = useLocalePath()

  return (
    <div>
      <Link
        href={lp(`/insights/${article.slug}`)}
        className={`block overflow-hidden rounded-[16px] transition-transform hover:-translate-y-1 ${CARD_BG[article.cardColor]}`}
      >
        <div className="relative">
          <div className="relative aspect-[16/10] w-full">
            <Image
              src={article.heroImage}
              alt={article.title}
              fill
              sizes="(max-width: 768px) 100vw, 33vw"
              className="rounded-t-[12px] object-cover"
            />
          </div>
          <span className="type-body-sm absolute top-3 left-3 rounded-full bg-white/90 px-3 py-1 font-mono font-medium tracking-wider text-neutral-700 uppercase backdrop-blur-sm">
            {article.categoryLabel}
          </span>
        </div>
        <div className="px-5 pt-5 pb-5">
          <h3
            className={`text-lg leading-snug font-medium ${light ? "text-aubergine-900" : "text-white"}`}
          >
            {article.title}
          </h3>
          <p
            className={`mt-2 line-clamp-2 text-sm ${light ? "text-neutral-500" : "text-white/70"}`}
          >
            {article.excerpt}
          </p>
          <div className="mt-4 flex items-center gap-2.5">
            {article.authorPhoto && (
              <Image
                src={article.authorPhoto}
                alt={article.author}
                width={28}
                height={28}
                className="rounded-full object-cover"
              />
            )}
            <span
              className={`text-sm ${light ? "text-neutral-500" : "text-white/70"}`}
            >
              {article.author}
            </span>
          </div>
        </div>
      </Link>
    </div>
  )
}

/* ───────────────────── Lexical renderer ─────────────────────
   The tree-walk lives in components/cms/lexical/render-lexical.tsx (shared with
   the landing-page richText block, AHD-8095). This file supplies only the
   article-specific link resolution + image-placeholder stripping below. */

const LOCALE_PREFIX_RE = /^\/(ch|nl)\/(en|de|fr|nl)\//

/** Fix broken CMS internal links (e.g. "/insights/tests/x" → "/tests/x") */
function fixInternalPath(path: string): string {
  return path.replace(
    /^\/insights\/(tests|biomarkers|what-we-detect)\//,
    "/$1/"
  )
}

/**
 * Article-specific Lexical options for the shared renderer. Link resolution:
 * dead-link rewrite → strip locale prefix → fixInternalPath → locale-prefix
 * internal / safeUrl external. `stripText` removes the inline image-placeholder
 * tokens the editor injects into article bodies.
 */
function buildArticleLexicalOptions(
  localePath: (path: string) => string,
  country: Country,
  locale: string
): LexicalRenderOptions {
  return {
    resolveHref: (raw) => {
      const rewritten = rewriteDeadLink(raw)
      const stripped = rewritten.startsWith("/")
        ? "/" +
          fixInternalPath(
            rewritten.replace(LOCALE_PREFIX_RE, "/").replace(/^\/+/, "")
          )
        : rewritten
      if (!stripped.startsWith("/")) return safeUrl(stripped)
      // Framer-era landing alias still present in some CMS article bodies.
      const bare =
        stripped === "/full-body-checkup" ? "/tests/full-body-mri" : stripped
      // A shared article can link to a page that is country-gated out of the
      // market it's served in (e.g. the CH-only insurance checker linked from a
      // DE-served article). Prefixing with the current country would 404, so
      // resolve gated-out links against the country where the page exists.
      if (!isPathAllowedInCountry(bare, country)) {
        const gate = gateForPath(bare)
        const canonicalCountry = gate && countriesForGatedPage(gate)[0]
        if (canonicalCountry) return `/${canonicalCountry}/${locale}${bare}`
      }
      return localePath(bare)
    },
    stripText: (text) =>
      text.replace(IMAGE_PLACEHOLDER_RE, "").replace(RAW_IMG_TAG_RE, ""),
  }
}

/* ───────────────────── Page ───────────────────── */

export function ArticleDetailClient({
  article,
  formattedDate,
  formattedUpdatedDate,
  related,
  cmsContent,
  cmsHeroImage,
  cmsHeroAlt,
  heroOverride,
  cmsAuthor,
  cmsReviewer,
  sources,
  relatedFaqs,
  cmsPostId,
}: {
  article: ArticleData
  formattedDate: string
  /** Localised "last modified" date, present only when the post was edited on a
   *  later day than it was published (see the insights article page). */
  formattedUpdatedDate?: string
  related: ArticleData[]
  cmsContent?: LexicalContent | null
  cmsHeroImage?: string | null
  cmsHeroAlt?: string | null
  heroOverride?: ReactNode
  cmsAuthor?: CmsAuthor | null
  cmsReviewer?: CmsAuthor | null
  sources?: LexicalContent | null
  relatedFaqs?: FaqItem[] | null
  /** Payload doc id + BCP47 locale — present so the editor build can stamp the
   *  body's text runs for inline editing. */
  cmsPostId?: string
}) {
  const ut = useTranslations("ui")
  const t = useTranslations("insights")
  const lp = useLocalePath()
  const locale = useLocale()
  const country = useCountry().code
  const lexicalOptions = buildArticleLexicalOptions(lp, country, locale)
  // CMS locale these docs were fetched in — the inline editor stamps it so a
  // draft write targets the matching Payload locale row. No-op in public prod.
  const editLocale = cmsLocale(locale, country)

  // Register the body as an editable region carrying the in-memory Lexical tree
  // as its value. EditModeHost's injector walks that tree offline and stamps the
  // matching leaf <span>s client-side on production (where the public HTML ships
  // no stamps). No DOM markup — coords + tree live in JS, and nothing runs until
  // a signed-in user opens the editor. (AHD-7425)
  const bodyRef = useEditRegion<HTMLDivElement>(
    // no-raw-ids: id is a CMS inline-editor hook argument, never rendered to a reader
    cmsPostId
      ? {
          collection: "blog-posts",
          id: cmsPostId,
          field: "content",
          locale: editLocale,
          kind: "lexical",
        }
      : null,
    cmsContent
  )

  // Article title scalar region. The <h1> renders `article.title`, which is the
  // localized `blog-posts.title` CMS field (see blogPostToArticle). Registered
  // only when the doc id is present; the public build ships no DOM stamps.
  // (AHD-7515)
  const titleRef = useEditRegion<HTMLHeadingElement>(
    // no-raw-ids: id is a CMS inline-editor hook argument, never rendered to a reader
    cmsPostId
      ? {
          collection: "blog-posts",
          id: cmsPostId,
          field: "title",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )

  // Author byline scalar regions. Registered only when the doc id is present;
  // the public build ships no DOM stamps. (AHD-7396)
  const authorNameRef = useEditRegion<HTMLSpanElement>(
    // no-raw-ids: id is a CMS inline-editor hook argument, never rendered to a reader
    cmsAuthor
      ? {
          collection: "authors",
          id: String(cmsAuthor.id),
          field: "name",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const reviewerNameRef = useEditRegion<HTMLSpanElement>(
    // no-raw-ids: id is a CMS inline-editor hook argument, never rendered to a reader
    cmsReviewer && cmsReviewer.type === "reviewer"
      ? {
          collection: "authors",
          id: String(cmsReviewer.id),
          field: "name",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const authorCardNameRef = useEditRegion<HTMLAnchorElement>(
    // no-raw-ids: id is a CMS inline-editor hook argument, never rendered to a reader
    cmsAuthor
      ? {
          collection: "authors",
          id: String(cmsAuthor.id),
          field: "name",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const authorCardRoleRef = useEditRegion<HTMLParagraphElement>(
    // no-raw-ids: id is a CMS inline-editor hook argument, never rendered to a reader
    cmsAuthor
      ? {
          collection: "authors",
          id: String(cmsAuthor.id),
          field: "role",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const authorCardBioRef = useEditRegion<HTMLParagraphElement>(
    // no-raw-ids: id is a CMS inline-editor hook argument, never rendered to a reader
    cmsAuthor && cmsAuthor.bio
      ? {
          collection: "authors",
          id: String(cmsAuthor.id),
          field: "bio",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )

  const categoryLabels: Record<string, string> = {
    "early-detection": t("earlyDetection"),
    technology: t("technology"),
    "nutrition-lifestyle": t("nutritionLifestyle"),
    "cost-insurance": t("costInsurance"),
  }
  const translatedCategoryLabel =
    categoryLabels[article.category] || article.categoryLabel

  useEffect(() => {
    track.articleView(article.slug, article.title, article.categoryLabel)
  }, [article.slug, article.title, article.categoryLabel])

  return (
    <>
      <article className="mx-auto max-w-3xl px-6 pt-12 pb-16 md:pt-16">
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-2 text-sm"
        >
          <Link
            href={lp("/insights")}
            className="hover:text-aubergine-900 flex items-center gap-1 text-neutral-400 transition-colors"
          >
            <ArrowLeftIcon aria-hidden="true" />
            <span className="type-body-sm font-mono font-medium tracking-[0.15em] uppercase">
              {t("pageLabel").toUpperCase()}
            </span>
          </Link>
          <span className="text-neutral-300" aria-hidden="true">
            &middot;
          </span>
          <span className="type-body-sm font-mono font-medium tracking-[0.15em] text-neutral-400 uppercase">
            {translatedCategoryLabel}
          </span>
        </nav>

        {/* Category badge */}
        <span className="type-body-sm mt-6 inline-block rounded-full bg-neutral-100 px-3 py-1 font-mono font-medium tracking-wider text-neutral-600 uppercase">
          {translatedCategoryLabel}
        </span>

        {/* Title */}
        <div className="mt-4 flex items-start justify-between gap-4">
          <h1
            ref={titleRef}
            className="text-aubergine-900 text-3xl font-normal md:text-4xl"
          >
            {article.title}
          </h1>
          <ShareButton />
        </div>

        {/* Meta */}
        <p className="mt-2 text-sm text-neutral-400">
          {formattedDate}
          {article.readTime ? ` · ${article.readTime}` : ""}
          {formattedUpdatedDate
            ? ` · ${t("lastUpdated", { date: formattedUpdatedDate })}`
            : ""}
        </p>

        {/* Byline */}
        {(cmsAuthor || cmsReviewer) && (
          <div className="mt-4 flex flex-wrap items-center gap-4">
            {cmsAuthor && (
              <Link
                href={lp(`/authors/${cmsAuthor.slug}`)}
                className="group flex items-center gap-2"
              >
                {typeof cmsAuthor.photo === "object" &&
                  cmsAuthor.photo?.url && (
                    <Image
                      src={cmsMediaUrl(cmsAuthor.photo.url)}
                      alt={cmsAuthor.name}
                      width={32}
                      height={32}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  )}
                <span className="group-hover:text-aubergine-900 text-sm text-neutral-600 transition-colors">
                  {ut("writtenBy")}{" "}
                  <span
                    ref={authorNameRef}
                    className="text-aubergine-900 font-medium"
                  >
                    {cmsAuthor.name}
                  </span>
                </span>
              </Link>
            )}
            {cmsReviewer && cmsReviewer.type === "reviewer" && (
              <Link
                href={lp(`/authors/${cmsReviewer.slug}`)}
                className="group flex items-center gap-2"
              >
                {typeof cmsReviewer.photo === "object" &&
                  cmsReviewer.photo?.url && (
                    <Image
                      src={cmsMediaUrl(cmsReviewer.photo.url)}
                      alt={cmsReviewer.name}
                      width={32}
                      height={32}
                      className="h-8 w-8 rounded-full object-cover"
                    />
                  )}
                <span className="group-hover:text-aubergine-900 text-sm text-neutral-600 transition-colors">
                  {ut("reviewedBy")}{" "}
                  <span
                    ref={reviewerNameRef}
                    className="text-aubergine-900 font-medium"
                  >
                    {cmsReviewer.name}
                  </span>
                </span>
              </Link>
            )}
          </div>
        )}

        {/* Hero image */}
        <div className="relative mt-8 aspect-[2/1] w-full">
          {heroOverride ?? (
            <Image
              src={cmsHeroImage || article.heroImage}
              alt={cmsHeroAlt || article.title}
              fill
              priority
              sizes="(max-width: 768px) 100vw, 720px"
              className="rounded-[16px] object-cover"
            />
          )}
        </div>

        {/* Body */}
        <div ref={bodyRef} className="mx-auto mt-10 max-w-3xl">
          {cmsContent?.root?.children?.length ? (
            <RenderLexical content={cmsContent} options={lexicalOptions} />
          ) : (
            <p className="text-lg leading-relaxed text-neutral-500">
              Content coming soon.
            </p>
          )}
        </div>

        {/* Sources */}
        {sources && (
          <details className="group mt-8 border-t border-neutral-200 pt-4">
            <summary className="type-body-sm flex cursor-pointer list-none items-center gap-2 font-normal text-neutral-500 [&::-webkit-details-marker]:hidden">
              <svg
                className="h-3.5 w-3.5 shrink-0 transition-transform group-open:rotate-90"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="m9 18 6-6-6-6" />
              </svg>
              Sources
            </summary>
            <div className="type-body-sm mt-3 [overflow-wrap:anywhere] text-neutral-500">
              <RenderLexical content={sources} options={lexicalOptions} />
            </div>
          </details>
        )}

        {/*
          Related FAQs.
          Visible only — no FAQPage JSON-LD here. The canonical schema home
          for each Q&A is `/faq/<slug>` (or whatever the editor tagged via the
          FAQ `pages` field), matching the high-fanout-template pattern from
          PR #759 that keeps Google from flagging duplicates.
        */}
        {relatedFaqs && relatedFaqs.length > 0 && (
          <section className="mt-12 border-t border-neutral-200 pt-10">
            <div>
              <h2 className="text-aubergine-900 text-2xl font-normal md:text-3xl">
                {t("faqHeading")}
              </h2>
            </div>
            <div className="mt-6">
              <FaqAccordion
                items={relatedFaqs.map((f) => ({
                  question: f.question,
                  answer: f.answer,
                  slug: f.slug,
                  ...(f.updatedAt ? { updatedAt: f.updatedAt } : {}),
                }))}
              />
            </div>
          </section>
        )}

        {/* Author card */}
        {cmsAuthor && (
          <div className="mt-12 flex items-start gap-4 rounded-[16px] bg-white p-6">
            {typeof cmsAuthor.photo === "object" && cmsAuthor.photo?.url && (
              <Image
                src={cmsMediaUrl(cmsAuthor.photo.url)}
                alt={cmsAuthor.name}
                width={56}
                height={56}
                className="h-14 w-14 flex-shrink-0 rounded-full object-cover"
              />
            )}
            <div>
              <Link
                ref={authorCardNameRef}
                href={lp(`/authors/${cmsAuthor.slug}`)}
                className="text-aubergine-900 text-base font-medium hover:underline"
              >
                {cmsAuthor.name}
              </Link>
              <p ref={authorCardRoleRef} className="text-sm text-neutral-500">
                {cmsAuthor.role}
              </p>
              {cmsAuthor.bio && (
                <p
                  ref={authorCardBioRef}
                  className="mt-1 line-clamp-2 text-sm text-neutral-400"
                >
                  {cmsAuthor.bio}
                </p>
              )}
            </div>
          </div>
        )}
      </article>

      {/* Related articles */}
      {related.length > 0 && (
        <section className="px-6 pb-16">
          <div className="mx-auto max-w-7xl">
            <div>
              <h2 className="text-aubergine-900 text-2xl font-normal md:text-3xl">
                {t("relatedArticles")}
              </h2>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {related.map((a, i) => (
                <RelatedCard key={a.slug} article={a} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA banner */}
      <CtaBanner
        title={ut("takeControl")}
        subtitle={ut("takeControlSubtitle")}
        imageSrc={blobMediaUrl("/images/homepage/cta-bottom.jpeg")}
        primaryCta={{ label: ut("bookCheckup"), href: "#" }}
        secondaryCta={{
          label: ut("learnHowItWorks"),
          href: lp("/how-it-works"),
        }}
      />

      {/* Footer */}
    </>
  )
}
